import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Shield, LogOut, Menu } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import SuperAdminSidebar from './SuperAdminSidebar';
import SuperAdminAttendanceRequests from './SuperAdminAttendanceRequests';
import SuperAdminPerformance from './SuperAdminPerformance';
import SuperAdminOnboardingRequests from './SuperAdminOnboardingRequests';
import SuperAdminOfferLetters from './SuperAdminOfferLetters';
import AttendanceManagement from '../admin/AttendanceManagement/AttendanceManagement';
import DateWiseAttendance from '../admin/AttendanceManagement/DateWise/DateWiseAttendance';
import DateSpecificAttendance from '../admin/AttendanceManagement/DateWise/DateSpecificAttendance';
import EmployeeWiseAttendance from '../admin/AttendanceManagement/EmployeeWise/EmployeeWiseAttendance';
import EmployeeAttendanceCalendar from '../admin/AttendanceManagement/EmployeeWise/EmployeeAttendanceCalendar';
import AttendanceOverview from '../admin/AttendanceManagement/Overview/AttendanceOverview';
import PayrollManagement from '../admin/PayrollManagement';
import PayrollEmployeeDetail from '../admin/PayrollEmployeeDetail';
import EmployeeManagement from '../admin/EmployeeManagement';
import ProbationStatus from '../admin/ProbationStatus';
import InternshipStatus from '../admin/InternshipStatus';
import FullAndFinalSettlementManagement from '../admin/FullAndFinalSettlementManagement';
import BirthdayManagement from '../admin/BirthdayManagement';
import BirthdayPopup from '../common/BirthdayPopup';
import BirthdayBell from '../common/BirthdayBell';
import api from '../../services/api';

const SuperAdminDashboard = () => {
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [attendancePendingCount, setAttendancePendingCount] = useState(null);
  const [onboardingPendingCount, setOnboardingPendingCount] = useState(null);
  const [offerLetterPendingCount, setOfferLetterPendingCount] = useState(null);
  const [probationPendingCount, setProbationPendingCount] = useState(null);
  const [internshipPendingCount, setInternshipPendingCount] = useState(null);
  const [fullAndFinalPendingCount, setFullAndFinalPendingCount] = useState(null);
  const controllerRef = useRef(null);

  useEffect(() => {
    const saved = window.localStorage.getItem('superadmin-sidebar-collapsed');
    if (saved === 'true') {
      setSidebarCollapsed(true);
    }
  }, []);

  const toggleSidebarCollapsed = () => {
    setSidebarCollapsed((prev) => {
      const next = !prev;
      window.localStorage.setItem('superadmin-sidebar-collapsed', String(next));
      return next;
    });
  };

  const fetchSidebarCounts = useCallback(() => {
    if (controllerRef.current) {
      controllerRef.current.abort();
    }

    const controller = new AbortController();
    controllerRef.current = controller;

    Promise.allSettled([
      api.get('/superadmin/attendance-requests/stats/summary', { signal: controller.signal }),
      api.get('/superadmin/onboarding-requests/stats/summary', { signal: controller.signal }),
      api.get('/superadmin/offer-letters/stats/summary', { signal: controller.signal }),
      api.get('/admin/probation/stats/summary', { signal: controller.signal }),
      api.get('/admin/internship/stats/summary', { signal: controller.signal }),
      api.get('/superadmin/full-and-final/stats/summary', { signal: controller.signal }),
    ])
      .then((results) => {
        const [att, onb, offer, probation, internship, fullAndFinal] = results;
        if (att.status === 'fulfilled') {
          setAttendancePendingCount(att.value.data?.pending ?? null);
        }
        if (onb.status === 'fulfilled') {
          const d = onb.value.data;
          const summary = d?.summary ?? d;
          setOnboardingPendingCount(summary?.pending ?? d?.pending ?? null);
        }
        if (offer.status === 'fulfilled') {
          setOfferLetterPendingCount(offer.value.data?.pending ?? null);
        }
        if (probation.status === 'fulfilled') {
          setProbationPendingCount(probation.value.data?.pending ?? null);
        }
        if (internship.status === 'fulfilled') {
          setInternshipPendingCount(internship.value.data?.pending ?? null);
        }
        if (fullAndFinal.status === 'fulfilled') {
          setFullAndFinalPendingCount(fullAndFinal.value.data?.pending ?? null);
        }
      })
      .catch((err) => {
        if (err?.name === 'CanceledError' || err?.name === 'AbortError') return;
        console.error('Failed to fetch sidebar counts', err);
      });
  }, []);

  useEffect(() => {
    const id = setInterval(fetchSidebarCounts, 15000);
    fetchSidebarCounts();

    return () => {
      clearInterval(id);
      if (controllerRef.current) {
        controllerRef.current.abort();
      }
    };
  }, [fetchSidebarCounts]);

  return (
    <div className="flex min-h-screen bg-slate-50 text-slate-900">
      <BirthdayPopup />
      <SuperAdminSidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        collapsed={sidebarCollapsed}
        onToggleCollapse={toggleSidebarCollapsed}
        attendancePendingCount={attendancePendingCount}
        onboardingPendingCount={onboardingPendingCount}
        offerLetterPendingCount={offerLetterPendingCount}
        probationPendingCount={probationPendingCount}
        internshipPendingCount={internshipPendingCount}
        fullAndFinalPendingCount={fullAndFinalPendingCount}
      />

      <div className={`flex min-w-0 flex-1 flex-col overflow-hidden transition-[padding] duration-300 ${sidebarCollapsed ? 'lg:pl-24' : 'lg:pl-72'}`}>
        <header className="border-b border-white/70 bg-white/80 shadow-[0_1px_0_rgba(15,23,42,0.04)] backdrop-blur-xl">
          <div className="flex h-20 items-center justify-between gap-4 px-5 sm:px-6 lg:px-8">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden rounded-xl border border-slate-200 bg-white p-2.5 text-slate-500 shadow-sm transition hover:border-indigo-200 hover:text-indigo-700"
              >
                <Menu className="h-5 w-5" />
              </button>
              <div className="flex items-center gap-3">
                <div className="hidden h-10 w-10 items-center justify-center rounded-2xl bg-gradient-to-br from-indigo-500 to-violet-600 text-white shadow-md shadow-indigo-200/70 lg:flex">
                  <Shield className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-slate-500">
                    Superadmin Workspace
                  </p>
                  <h1 className="mt-0.5 text-xl font-semibold text-slate-900 sm:text-2xl">
                    Super Admin Panel
                  </h1>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-3 sm:gap-4">
              <BirthdayBell />
              <div className="hidden lg:flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-emerald-700">
                <span className="h-2 w-2 rounded-full bg-emerald-500 superadmin-pulse-dot" />
                Live updates
              </div>
              <div className="hidden flex-col items-end sm:flex">
                <span className="text-sm font-semibold text-slate-900 leading-tight">
                  {user?.name || 'Super Admin'}
                </span>
                <span className="text-xs text-slate-500 leading-tight">System Super Admin</span>
              </div>
              <div className="hidden h-8 w-px bg-slate-200 sm:block" />
              <button
                onClick={logout}
                className="inline-flex items-center gap-1.5 rounded-xl border border-rose-200 bg-rose-600 px-4 py-2.5 text-sm font-medium text-white shadow-sm transition hover:bg-rose-700"
              >
                <LogOut className="h-4 w-4" />
                <span>Logout</span>
              </button>
            </div>
          </div>
        </header>

        <main className="relative flex-1 overflow-x-hidden overflow-y-auto superadmin-bg-grid">
          <div className="pointer-events-none absolute inset-0 overflow-hidden">
            <div className="superadmin-ambient-orb superadmin-ambient-orb-a" />
            <div className="superadmin-ambient-orb superadmin-ambient-orb-b" />
          </div>
          <div className="mx-auto w-full max-w-[1600px] px-4 py-5 sm:px-6 sm:py-6 lg:px-8 lg:py-8">
            <div className="relative rounded-[2rem] border border-white/80 bg-white/78 p-4 shadow-[0_18px_60px_rgba(15,23,42,0.08)] backdrop-blur-xl superadmin-shell-enter sm:p-6 lg:p-8">
              <div className="pointer-events-none absolute inset-0 superadmin-noise-overlay" />
              <div className="pointer-events-none absolute left-0 right-0 top-0 h-[3px] superadmin-sheen-band" />
              <div className="relative z-10">
                <Routes>
                  <Route path="/" element={<Navigate to="/superadmin/dashboard" />} />
                  <Route
                    path="/dashboard"
                    element={<SuperAdminAttendanceRequests onSidebarPendingRefresh={fetchSidebarCounts} />}
                  />
                  <Route
                    path="/attendance-requests"
                    element={<SuperAdminAttendanceRequests onSidebarPendingRefresh={fetchSidebarCounts} />}
                  />
                  <Route
                    path="/onboarding-requests"
                    element={<SuperAdminOnboardingRequests onSidebarPendingRefresh={fetchSidebarCounts} />}
                  />
                  <Route
                    path="/onboarding-requests/:id"
                    element={<SuperAdminOnboardingRequests onSidebarPendingRefresh={fetchSidebarCounts} />}
                  />
                  <Route
                    path="/offer-letters"
                    element={<SuperAdminOfferLetters onSidebarPendingRefresh={fetchSidebarCounts} />}
                  />
                  <Route
                    path="/offer-letters/:id"
                    element={<SuperAdminOfferLetters onSidebarPendingRefresh={fetchSidebarCounts} />}
                  />
                  <Route path="/performance" element={<SuperAdminPerformance />} />
                  <Route path="/attendance" element={<AttendanceManagement basePath="/superadmin/attendance" />} />
                  <Route path="/attendance/date-wise" element={<DateWiseAttendance basePath="/superadmin/attendance" />} />
                  <Route path="/attendance/date-wise/:date" element={<DateSpecificAttendance basePath="/superadmin/attendance" />} />
                  <Route path="/attendance/employee-wise" element={<EmployeeWiseAttendance basePath="/superadmin/attendance" />} />
                  <Route path="/attendance/employee-wise/:employeeId" element={<EmployeeAttendanceCalendar basePath="/superadmin/attendance" />} />
                  <Route path="/attendance/overview" element={<AttendanceOverview basePath="/superadmin/attendance" />} />
                  <Route path="/payroll" element={<PayrollManagement basePath="/superadmin/payroll" />} />
                  <Route path="/payroll/:employeeId" element={<PayrollEmployeeDetail basePath="/superadmin/payroll" />} />
                  <Route path="/employees" element={<EmployeeManagement />} />
                  <Route path="/birthdays" element={<BirthdayManagement />} />
                  <Route
                    path="/probation"
                    element={<ProbationStatus onSidebarCountsRefresh={fetchSidebarCounts} />}
                  />
                  <Route
                    path="/internship"
                    element={<InternshipStatus onSidebarCountsRefresh={fetchSidebarCounts} />}
                  />
                  <Route
                    path="/full-and-final"
                    element={<FullAndFinalSettlementManagement portal="superadmin" onSidebarCountsRefresh={fetchSidebarCounts} />}
                  />
                </Routes>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default SuperAdminDashboard;
